"use client";

import { c as _c } from "react/compiler-runtime";
import { IconCircleCheck } from "central-icons/IconCircleCheck";
import { IconCircleX } from "central-icons/IconCircleX";
import { useState } from "react";
import "@/styles/enter-animation.css";
import DemoFooter from "@/components/preview/demo-footer";
import DemoWrapper from "@/components/preview/demo-wrapper";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SLIDE_HEIGHT } from "./slide-height";
type AnimationType = "none" | "grouped" | "individual";
const title = "Build software that never breaks";
const description = "Interfere is the experience layer for modern product teams.";
const descriptionRest = "Detect, triage, and fix bugs automatically.";
const MODES = [{
  icon: IconCircleX,
  label: "Block",
  value: "none"
}, {
  icon: IconCircleCheck,
  label: "Sections",
  value: "grouped"
}, {
  icon: IconCircleCheck,
  label: "Individual",
  value: "individual"
}] as const;
const TITLE_TYPE = "text-start font-medium text-4xl text-gray-1200 tracking-tight sm:text-5xl";
const TITLE_WORD_STAGGER = ["[--stagger:1]", "[--stagger:2]", "[--stagger:3]", "[--stagger:4]", "[--stagger:5]"];
const MODE_CLASSES: Record<AnimationType, {
  actions?: string;
  buttonWrap?: [string, string];
  description?: string;
  root: string;
  title?: string;
}> = {
  grouped: {
    actions: "animate-enter [--stagger:3]",
    description: "animate-enter [--stagger:2]",
    root: "animate-enter-individual p-6",
    title: "animate-enter [--stagger:1]"
  },
  individual: {
    buttonWrap: ["animate-enter [--stagger:7]", "animate-enter [--stagger:8]"],
    description: "animate-enter [--stagger:6]",
    root: "animate-enter-individual p-6"
  },
  none: {
    root: "animate-enter px-6 py-0"
  }
};
export default function EnterAnimation() {
  const $ = _c(46);
  const [selected, setSelected] = useState("none");
  const mode = MODE_CLASSES[selected];
  const {
    buttonWrap
  } = mode;
  let T0;
  let buttons;
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== mode.root || $[1] !== mode.title || $[2] !== selected) {
    const words = title.split(" ");
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = [<Button key="demo" rounded={true} size="lg" type="button" variant="primary">Request a demo</Button>, <Button key="waitlist" rounded={true} size="lg" type="button" variant="secondary">Join waitlist</Button>];
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    buttons = t6;
    T0 = DemoWrapper;
    t4 = "relative";
    t5 = SLIDE_HEIGHT;
    if ($[12] !== mode.root) {
      t2 = cn("flex h-full w-full select-none flex-col items-start justify-center gap-4 sm:gap-5 sm:px-16 sm:py-8", mode.root);
      $[12] = mode.root;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    t3 = selected;
    t0 = "flex w-full flex-col gap-3";
    t1 = selected === "individual" ? <div className="max-w-120 text-pretty">{words.map((word, i) => <span className={cn("animate-enter-title", TITLE_TYPE, TITLE_WORD_STAGGER[i])} key={word}>{word}{i < words.length - 1 && <> </>}</span>)}</div> : <div className={cn("max-w-120 text-pretty", TITLE_TYPE, mode.title)}>{title}</div>;
    $[0] = mode.root;
    $[1] = mode.title;
    $[2] = selected;
    $[3] = T0;
    $[4] = buttons;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    T0 = $[3];
    buttons = $[4];
    t0 = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
  }
  let t6;
  if ($[14] !== mode.description) {
    t6 = cn("max-w-120 text-pretty text-start text-black/50 dark:text-white/50", mode.description);
    $[14] = mode.description;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="hidden sm:inline">{` ${descriptionRest}`}</span>;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6) {
    t8 = <div className={t6}>{description}{t7}</div>;
    $[17] = t6;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t0 || $[20] !== t1 || $[21] !== t8) {
    t9 = <div className={t0}>{t1}{t8}</div>;
    $[19] = t0;
    $[20] = t1;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== mode.actions) {
    t10 = cn("flex gap-2", mode.actions);
    $[23] = mode.actions;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== buttonWrap || $[26] !== buttons) {
    t11 = buttonWrap ? buttons.map((button, i_0) => <div className={buttonWrap[i_0]} key={button.key}>{button}</div>) : buttons;
    $[25] = buttonWrap;
    $[26] = buttons;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t10 || $[29] !== t11) {
    t12 = <div className={t10}>{t11}</div>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t12 || $[32] !== t2 || $[33] !== t3 || $[34] !== t9) {
    t13 = <div className={t2} key={t3}>{t9}{t12}</div>;
    $[31] = t12;
    $[32] = t2;
    $[33] = t3;
    $[34] = t9;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== selected) {
    t14 = MODES.map(t15 => {
      const {
        icon: Icon,
        label,
        value
      } = t15;
      return <Button className={cn("w-full", selected === value && "bg-gray-200")} key={value} leftIcon={<Icon className="size-4 text-gray-1000" />} onClick={() => setSelected(value)} size="sm" type="button" variant="secondary">{label}</Button>;
    });
    $[36] = selected;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t14) {
    t15 = <DemoFooter className="z-10 flex flex-col gap-2 sm:flex-row">{t14}</DemoFooter>;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== T0 || $[41] !== t13 || $[42] !== t15 || $[43] !== t4 || $[44] !== t5) {
    t16 = <T0 className={t4} height={t5}>{t13}{t15}</T0>;
    $[40] = T0;
    $[41] = t13;
    $[42] = t15;
    $[43] = t4;
    $[44] = t5;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
}